/**
 * InfluencerPayments.jsx — workspace-wide unpaid + partial creator payments.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { IndianRupee, Users2, TrendingUp, ArrowUpRight } from "lucide-react";

export default function InfluencerPayments() {
  const { activeWorkspaceId: wsId } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${wsId}/influencer-payments/pending`);
      setRows(data?.rows || []);
    } catch (e) { toast.error("Load failed"); } finally { setLoading(false); }
  }, [wsId]);
  useEffect(() => { load(); }, [load]);

  const totalOutstanding = rows.reduce((s, r) => s + (r.outstanding_inr || 0), 0);

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="inf-payments-page">
      <PageHeader
        title="Creator payments"
        icon={IndianRupee}
        description="Every unpaid or partial creator across all campaigns."
        actions={(
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" size="sm"><Link to="/app/influencers"><Users2 className="w-3.5 h-3.5 mr-1.5" /> Directory</Link></Button>
            <Button asChild variant="outline" size="sm"><Link to="/app/influencers/campaigns"><TrendingUp className="w-3.5 h-3.5 mr-1.5" /> Campaigns</Link></Button>
          </div>
        )}
      />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="border border-border p-3">
          <div className="text-[10px] uppercase text-muted-foreground">Pending creators</div>
          <div className="text-2xl font-bold tabular-nums mt-1">{rows.length}</div>
        </div>
        <div className={`border p-3 ${totalOutstanding > 0 ? "border-amber-400 bg-amber-50/50" : "border-border"}`}>
          <div className="text-[10px] uppercase text-muted-foreground">Total outstanding</div>
          <div className="text-2xl font-bold tabular-nums mt-1">₹{totalOutstanding.toLocaleString("en-IN")}</div>
        </div>
        <div className="border border-border p-3">
          <div className="text-[10px] uppercase text-muted-foreground">Avg per creator</div>
          <div className="text-2xl font-bold tabular-nums mt-1">₹{rows.length ? Math.round(totalOutstanding / rows.length).toLocaleString("en-IN") : 0}</div>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground p-6">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState icon={IndianRupee} title="All caught up" description="No pending payments right now." testId="inf-pay-empty" />
      ) : (
        <div className="border border-border overflow-x-auto" data-testid="inf-pay-table">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 border-b border-border">
              <tr className="text-left text-[10px] uppercase text-muted-foreground">
                <th className="px-3 py-2">Creator</th>
                <th className="px-3 py-2">Campaign</th>
                <th className="px-3 py-2 text-right">Agreed</th>
                <th className="px-3 py-2 text-right">Paid</th>
                <th className="px-3 py-2 text-right">Outstanding</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-muted/20" data-testid={`inf-pay-row-${r.id}`}>
                  <td className="px-3 py-2">
                    <Link to={`/app/influencers/${r.influencer_id}`} className="flex items-center gap-2 hover:text-primary">
                      <div className="w-7 h-7 bg-secondary flex items-center justify-center text-[10px] font-bold">{r.influencer?.name?.[0]}</div>
                      <div className="min-w-0"><div className="font-medium truncate">{r.influencer?.name || "(deleted)"}</div><div className="text-[10px] text-muted-foreground">@{r.influencer?.primary_handle}</div></div>
                    </Link>
                  </td>
                  <td className="px-3 py-2">
                    <Link to={`/app/influencers/campaigns/${r.campaign_id}`} className="hover:text-primary">
                      <div className="text-[10px] font-mono text-muted-foreground">{r.campaign?.code}</div>
                      <div className="truncate max-w-[200px]">{r.campaign?.name}</div>
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">₹{(r.agreed_cost_inr || 0).toLocaleString("en-IN")}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">₹{(r.paid_amount_inr || 0).toLocaleString("en-IN")}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums font-semibold text-amber-700">₹{(r.outstanding_inr || 0).toLocaleString("en-IN")}</td>
                  <td className="px-3 py-2"><span className={`text-[10px] uppercase tracking-wide ${r.payment_status === "partial" ? "text-amber-700" : "text-rose-600"}`}>{r.payment_status}</span></td>
                  <td className="px-3 py-2 text-right">
                    <Link to={`/app/influencers/campaigns/${r.campaign_id}`} className="text-muted-foreground hover:text-primary"><ArrowUpRight className="w-4 h-4" /></Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
